import { Box, Checkbox, Flex, Spinner, Text } from "@chakra-ui/react";
import moment from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/ApiClient/apiClientAxios";
// import { demoEdssCaseSearch } from "../../utils/demoData";
import EdssSearchForm from "./EdssSearchForm";
import EdssTable from "./EdssTable";

// Constants
const PERSIST_KEY = "edssPersistedSearch";
const RECENT_CASE_KEY = "recentClickCase";
const DATE_FORMAT = "YYYYMMDD";

/**
 * EDSS Case Search Popup Component
 * Provides search functionality with optional persistence
 * @param {Function} onClose - Callback to close the popup
 */
const EdssPopup = ({ onClose }) => {
  // State management
  const [searchResult, setSearchResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [persistSearch, setPersistSearch] = useState(false);
  const [error, setError] = useState(null);

  // Form management
  const { register, handleSubmit, setValue, watch, reset } = useForm({
    mode: "onSubmit",
    defaultValues: {},
  });

  // Refs
  const navigate = useNavigate();
  const previousDataRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Format date fields in the form data
   * @param {Object} data - Raw form data
   * @returns {Object} Formatted data with dates converted
   */
  const formatFormData = useCallback((data) => {
    const formattedData = { ...data };

    Object.keys(formattedData).forEach((key) => {
      if (key.toUpperCase().includes("DATE") && formattedData[key]) {
        formattedData[key] = moment(formattedData[key]).format(DATE_FORMAT);
      }
    });

    return formattedData;
  }, []);

  /**
   * Check if form data has changed
   * @param {Object} newData - New form data
   * @returns {boolean} True if data has changed
   */
  const hasDataChanged = useCallback((newData) => {
    return JSON.stringify(newData) !== JSON.stringify(previousDataRef.current);
  }, []);

  /**
   * Perform the search API call
   * @param {Object} formattedData - Formatted search criteria
   */
  const performSearch = useCallback(
    async (formattedData) => {
      if (!hasDataChanged(formattedData)) {
        console.log("Search criteria unchanged. Skipping API call.");
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await apiClient.post("/searchEDSSCase", formattedData);

        if (!isMountedRef.current) return;

        const result = response.data || [];
        setSearchResult(result);
        previousDataRef.current = formattedData;

        // Persist if enabled
        if (persistSearch) {
          localStorage.setItem(
            PERSIST_KEY,
            JSON.stringify({
              data: formattedData,
              result,
              timestamp: Date.now(),
            }),
          );
        }
      } catch (err) {
        if (!isMountedRef.current) return;

        console.error("EDSS search error:", err);
        setError(
          err.response?.data?.message || "Failed to fetch search results",
        );
        setSearchResult([]);
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [hasDataChanged, persistSearch],
  );

  /**
   * Form submission handler
   * @param {Object} data - Raw form data
   */
  const onSubmit = useCallback(
    async (data) => {
      const formattedData = formatFormData(data);
      await performSearch(formattedData);
    },
    [formatFormData, performSearch],
  );

  /**
   * Convert date from YYYYMMDD to YYYY-MM-DD format
   * @param {string} dateString - Date in YYYYMMDD format
   * @returns {string} Date in YYYY-MM-DD format
   */
  const convertDateForForm = useCallback((dateString) => {
    if (!dateString) return "";
    if (dateString.includes("-")) return dateString;
    if (dateString.length === 8) {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${year}-${month}-${day}`;
    }

    return "";
  }, []);

  /**
   * Load and restore persisted search on mount
   */
  useEffect(() => {
    isMountedRef.current = true;
    const loadPersistedSearch = async () => {
      try {
        const persistedString = localStorage.getItem(PERSIST_KEY);
        if (!persistedString) return;
        const persisted = JSON.parse(persistedString);
        if (persisted?.data && persisted?.result) {
          // Convert dates back to YYYY-MM-DD format for form inputs
          const restoredData = { ...persisted.data };
          Object.keys(restoredData).forEach((key) => {
            if (key.toUpperCase().includes("DATE") && restoredData[key]) {
              restoredData[key] = convertDateForForm(restoredData[key]);
            }
          });

          reset(restoredData);

          setSearchResult(persisted.result);
          setPersistSearch(true);

          previousDataRef.current = null;

          setTimeout(() => {
            if (isMountedRef.current) {
              handleSubmit(onSubmit)();
            }
          }, 100);
        }
      } catch (err) {
        console.error("Failed to load persisted search:", err);
        localStorage.removeItem(PERSIST_KEY);
      }
    };

    loadPersistedSearch();

    return () => {
      isMountedRef.current = false;
    };
  }, [reset, handleSubmit, onSubmit, convertDateForForm]);

  /**
   * Handle persist checkbox toggle
   * @param {Event} e - Change event
   */
  const handlePersistToggle = useCallback(
    (e) => {
      const checked = e.target.checked;
      setPersistSearch(checked);

      if (!checked) {
        localStorage.removeItem(PERSIST_KEY);
        previousDataRef.current = null;
      } else if (searchResult.length > 0 && previousDataRef.current) {
        // Save current search when enabling persist
        localStorage.setItem(
          PERSIST_KEY,
          JSON.stringify({
            data: previousDataRef.current,
            result: searchResult,
            timestamp: Date.now(),
          }),
        );
      }
    },
    [searchResult],
  );

  /**
   * Handle case selection and navigation
   * @param {string} ID - Case ID
   * @param {string} PersonID - Person ID
   * @param {Object} data - Case data
   */
  const handleOpenCase = useCallback(
    (ID, PersonID, data) => {
      try {
        localStorage.setItem(RECENT_CASE_KEY, JSON.stringify(data));
        navigate(`/CaseView/${ID}/${PersonID}`);
        onClose();
      } catch (err) {
        console.error("Failed to open case:", err);
      }
    },
    [navigate, onClose],
  );

  /**
   * Render content based on current state
   */
  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" minH="300px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Flex>
      );
    }

    if (error) {
      return (
        <Text textAlign="center" fontSize="lg" color="red.500" my={5}>
          {error}
        </Text>
      );
    }

    if (searchResult?.length > 0) {
      return (
        <EdssTable
          handleOpenCase={handleOpenCase}
          searchResult={searchResult}
          isLoading={isLoading}
        />
      );
    }

    return (
      <Text textAlign="center" fontSize="xl" color="gray.500" my={5}>
        No records found. Try adjusting your search criteria.
      </Text>
    );
  };

  return (
    <Box minH="70vh" position="relative">
      <Flex h="100%">
        {/* Search Form Section */}
        <Box
          w="30%"
          borderRight="2px solid"
          borderColor="gray.200"
          pr={4}
          overflowY="auto"
        >
          <EdssSearchForm
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            register={register}
            isLoading={isLoading}
            setValue={setValue}
            watch={watch}
          />

          <Checkbox
            mt={4}
            isChecked={persistSearch}
            onChange={handlePersistToggle}
            isDisabled={isLoading}
            colorScheme="blue"
          >
            Remember my search
          </Checkbox>
          <Text> Search results are limited to the first 150 records with broad querries. Please narrow criteria or use the <a href="https://wintercount.gpthd.org/Data" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">Case Dashboards</a>, the first dashboard at this link, for more effective browsing.  </Text>
        </Box>

        {/* Results Section */}
        <Box w="70%" pl={4} overflowY="auto">
          {renderContent()}
        </Box>
      </Flex>
    </Box>
  );
};

export default EdssPopup;
